import {allInternationalPhonePrefixesFormattedForDropdownAlpha2CountryCode} from "../settings/utils";
import {AgencyProps} from "../../api/model/agency";

export const MIN_LENGTH_NAME = 2

/**
 * Validates whether a given email address is in a proper format.
 *
 * This function checks if the input email adheres to a standard email format.
 * It also ensures the provided email is not empty and trims any unnecessary
 * whitespace from the input before validation.
 *
 * @param {string | null | undefined} email - The email address to be validated.
 * Can be a string, null, or undefined.
 *
 * @returns {boolean} True if the email is valid and adheres to the specified format, otherwise false.
 */
export const validateEmail = (email: string | null | undefined): boolean => {
    if (email?.length === 0) return false
    if (email) return (/^\w+([\.\-\+]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/.test(email.trim()))
    return false
}

/**
 * Validates the provided company name.
 *
 * This function checks if the given company name meets the minimum length requirement.
 * If the company name is null or undefined, the function returns false.
 *
 * @param {string | null} companyName - The company name to validate. Can be a string or null.
 * @returns {boolean} Returns true if the company name is non-null and meets the minimum length requirement; otherwise, false.
 */
export const validateCompanyName = (companyName: string | null): boolean => {
    if (companyName) return companyName.length >= MIN_LENGTH_NAME
    return false
}

/**
 * A function that validates a phone number by checking its length and ensuring it consists of numeric characters.
 *
 * This function determines if the provided telephone string meets the following criteria:
 * - The trimmed length of the telephone string must be greater than 6.
 * - The telephone string must be a representation of a valid numeric value.
 *
 * @param {string} telephone - The phone number input as a string to be validated.
 * @returns {boolean} - Returns true if the phone number is valid; otherwise, returns false.
 */
export const validatePhoneNumber = (telephone: string): boolean => telephone.trim().length > 6 && !isNaN(Number(telephone))

/**
 * Validates if the given street address meets the minimum length requirement.
 *
 * This function takes a string input representing a street address
 * and checks if its trimmed length is greater than or equal
 * to a predefined constant value `MIN_LENGTH_NAME`.
 *
 * @param {string} address - The string representing the street address to validate.
 * @returns {boolean} Returns true if the trimmed length of the address is greater than or equal to `MIN_LENGTH_NAME`, otherwise false.
 */
export const validateStreet = (address: string): boolean => address.trim().length >= MIN_LENGTH_NAME

/**
 * Validates a given zip code string to ensure it meets the minimum length requirement.
 *
 * The function checks if the zip code, after trimming leading and trailing whitespace,
 * has a length equal to or greater than the predefined minimum length constant (`MIN_LENGTH_NAME`).
 *
 * @param {string} zipCode - The zip code string to validate.
 * @returns {boolean} - Returns `true` if the zip code meets the minimum length requirement, otherwise `false`.
 */
export const validateZipCode = (zipCode: string): boolean => zipCode.trim().length >= MIN_LENGTH_NAME

/**
 * Validates a given city name based on its length after trimming whitespace.
 * Ensures that the trimmed city name meets the minimum length requirement.
 *
 * @constant
 * @type {function}
 * @param {string} city - The name of the city to validate.
 * @returns {boolean} Returns true if the trimmed city name length is greater than or equal to the minimum length, otherwise false.
 */
export const validateCity = (city: string): boolean => city.trim().length >= MIN_LENGTH_NAME

/**
 * Validates the given name string to ensure it meets specific criteria.
 *
 * The function checks that the input string, when trimmed of
 * any leading or trailing whitespace, has a length of at least 2
 * characters. Names with fewer than 2 characters are considered invalid.
 *
 * @param {string} name - The input string to validate.
 * @returns {boolean} Returns true if the trimmed name has a length of 2
 * or more; otherwise, false.
 */
export const validateName = (name: string): boolean => name.trim().length >= MIN_LENGTH_NAME

/**
 * Validates the provided contact person's name.
 *
 * This function verifies whether the input name meets a specified minimum length,
 * trimming any surrounding whitespace before performing the validation.
 * If the input name is null or undefined, the function returns false.
 *
 * @param {string | null | undefined} name - The contact person's name to validate.
 * @returns {boolean} Returns true if the name is valid and meets the minimum length requirement; otherwise, false.
 */
export const validateContactPerson = (name: string | null | undefined): boolean => {
    if (name) return name.trim().length >= MIN_LENGTH_NAME
    return false
}

/**
 * Validates a house number by checking if it is a non-empty string
 * after trimming any leading or trailing whitespace.
 *
 * @param {string} houseNumber - The house number to validate.
 * @returns {boolean} Returns true if the house number is non-empty after trimming; otherwise, false.
 */
export const validateHouseNumber = (houseNumber: string): boolean => houseNumber.trim().length >= 1

/**
 * Validates a given country value to check if it is not null or if it is undefined.
 *
 * @param {string | null | undefined} country - The country value to validate, which can be a string, null, or undefined.
 * @returns {boolean} Returns true if the country is not null or is undefined, otherwise false.
 */
export const validateCountry = (country: string | null | undefined): boolean => country !== null || typeof country === 'undefined'

/**
 * Validates the additional address information provided for correctness.
 *
 * This function checks whether the given additional address information
 * string has more than two characters after trimming whitespace. It ensures
 * that meaningful and complete additional address information has been provided.
 *
 * @param {string} additionalAddressInformation - The additional address information
 * to be validated.
 * @returns {boolean} Returns true if the trimmed string length is greater than
 * two, otherwise returns false.
 */
export const validateAdditionalAddressInformation = (additionalAddressInformation: string): boolean => additionalAddressInformation.trim().length > 2

/**
 * Validates whether a given first or last name meets the required conditions.
 *
 * The function checks if the provided name is not null or undefined, trims any
 * leading or trailing whitespace, and ensures the resulting string has a length
 * greater than or equal to the specified minimum length.
 *
 * @param {string | null | undefined} name - The first or last name to validate.
 * @returns {boolean} True if the name is valid and meets the minimum length requirement, otherwise false.
 */
export const validateFirstAndLastName = (name: string | null | undefined): boolean => {
    if (name) return name.trim().length >= MIN_LENGTH_NAME
    return false
}

/**
 * Checks if the provided travel components or journey name is valid based on its length.
 *
 * This function determines if the input `name` is valid by ensuring that:
 * - It is not null or undefined.
 * - The trimmed length of the string meets or exceeds the minimum required length (`MIN_LENGTH_NAME`).
 *
 * @param {string | null | undefined} name - The travel components or journey name to be validated.
 * @returns {boolean} Returns `true` if the name is valid; otherwise, returns `false`.
 */
export const validTravelComponentsOrJounrneyName = (name: string | null | undefined): boolean => {
    if (name) return name.trim().length >= MIN_LENGTH_NAME
    return false
}

/**
 * Validates whether the given agency parameter is a non-null object.
 *
 * This function checks if the provided `agency` argument is an instance of
 * an object and ensures that it is neither null nor undefined. The validation
 * is performed using the `instanceof` operator.
 *
 * @param {AgencyProps | null | undefined} agency - The agency parameter to validate.
 * @returns {boolean} Returns `true` if the agency is a valid object, otherwise `false`.
 */
export const validateAgency = (agency: AgencyProps | null | undefined): boolean => agency instanceof Object

/**
 * Validates an international phone prefix to check if it is neither `null` nor `undefined`.
 *
 * @function
 * @param {keyof typeof allInternationalPhonePrefixesFormattedForDropdownAlpha2CountryCode | null | undefined} phonePrefix - The phone prefix to be validated. Can be either a key from the international phone prefixes object, `null`, or `undefined`.
 * @returns {boolean} Returns `true` if the phone prefix is neither `null` nor `undefined`; otherwise, returns `false`.
 */
export const validateInternationalPhonePrefix = (phonePrefix: keyof typeof allInternationalPhonePrefixesFormattedForDropdownAlpha2CountryCode | null | undefined): boolean => phonePrefix !== null || typeof phonePrefix !== 'undefined'

